import React from 'react';
import { FiBriefcase, FiCircle, FiCreditCard, FiPrinter, FiShoppingBag, FiSun, FiMoon, FiLogOut } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { useTheme } from '../context/ThemeContext';
import { useModule } from '../context/ModuleContext';

export default function ModulePicker() {
  const { user, logout } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { theme, toggleTheme } = useTheme();
  const { setActiveModule } = useModule();

  const modules = [
    {
      id: 'custody',
      icon: <FiBriefcase size={36} />,
      title: t('module_custody'),
      desc: t('module_custody_desc'),
      enabled: true,
    },
    {
      id: 'gold',
      icon: <FiCircle size={36} />,
      title: t('module_gold'),
      desc: t('module_gold_desc'),
      enabled: true,
    },
    {
      id: 'loans',
      icon: <FiCreditCard size={36} />,
      title: t('module_loans'),
      desc: t('module_loans_desc'),
      enabled: true,
    },
    {
      id: 'receipts',
      icon: <FiPrinter size={36} />,
      title: t('module_receipts'),
      desc: t('module_receipts_desc'),
      enabled: true,
    },
    {
      id: 'gold_purchase',
      icon: <FiShoppingBag size={36} />,
      title: t('module_gold_purchase'),
      desc: t('module_gold_purchase_desc'),
      enabled: true,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 24px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--panel)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/logo192.png" alt="logo" style={{ height: '32px' }} onError={(e) => { e.target.style.display = 'none'; }} />
          <strong style={{ fontSize: '16px' }}>{t('brand_title')}</strong>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span className="muted small">{t('signed_in_as')}: <strong>{user?.name || user?.username}</strong></span>
          <select value={locale} onChange={(e) => setLocale(e.target.value)} className="lang-select">
            <option value="en">EN</option>
            <option value="pt-BR">PT</option>
          </select>
          <button type="button" className="btn btn-ghost btn-icon" onClick={toggleTheme} title="Theme">
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          <button type="button" className="btn btn-ghost" onClick={logout}>
            <FiLogOut style={{ marginRight: '6px' }} /> {t('logout')}
          </button>
        </div>
      </header>

      {/* Main */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px 24px',
          gap: '36px',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '700px' }}>
          <h1 style={{ margin: '0 0 8px 0' }}>{t('choose_module')}</h1>
          <p className="muted" style={{ margin: 0 }}>{t('choose_module_desc')}</p>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '20px',
            maxWidth: '1100px',
            width: '100%',
          }}
        >
          {modules.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => m.enabled && setActiveModule(m.id)}
              disabled={!m.enabled}
              style={{
                flex: '1 1 280px',
                maxWidth: '340px',
                minHeight: '220px',
                padding: '28px 24px',
                borderRadius: '16px',
                border: '1px solid var(--border)',
                background: 'var(--panel-2)',
                color: 'var(--text)',
                cursor: m.enabled ? 'pointer' : 'not-allowed',
                opacity: m.enabled ? 1 : 0.55,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '14px',
                textAlign: 'left',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (!m.enabled) return;
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.18)';
                e.currentTarget.style.borderColor = 'var(--accent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'var(--border)';
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  background: 'var(--panel)',
                  border: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--accent)',
                }}
              >
                {m.icon}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700 }}>{m.title}</div>
              <div className="muted small" style={{ lineHeight: 1.4 }}>{m.desc}</div>
              {!m.enabled && (
                <div className="muted small" style={{ marginTop: 'auto', fontStyle: 'italic' }}>
                  {t('coming_soon')}
                </div>
              )}
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}
